// See README.md in this folder for the tenant flag checklist.

import { Suspense, cloneElement, type ReactElement, type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import {
  ProviderStatus,
  useBooleanFlagValue,
  useOpenFeatureClientStatus,
} from '@openfeature/react-sdk';

import { useTenantFlagResolutionReady } from './featureFlagResolution';
import { flagDefaults, type TenantFlagKey } from './tenantFlags';

export interface UseTenantFlagOptions {
  suspendUntilReady?: boolean;
}

export function useTenantFlag(
  key: TenantFlagKey,
  { suspendUntilReady = false }: UseTenantFlagOptions = {}
): boolean {
  return useBooleanFlagValue(key, flagDefaults[key], { suspendUntilReady });
}

export interface FeatureGateProps {
  flag: TenantFlagKey;
  mode?: 'hide' | 'disable';
  suspendUntilReady?: boolean;
  fallback?: ReactNode;
  children: ReactElement;
}

interface FeatureGateInnerProps extends FeatureGateProps {
  isProviderReady: boolean;
}

function FeatureGateInner({
  flag,
  mode = 'hide',
  suspendUntilReady = true,
  fallback = null,
  isProviderReady,
  children,
}: FeatureGateInnerProps) {
  const enabled = useBooleanFlagValue(flag, flagDefaults[flag], { suspendUntilReady: false });

  if (suspendUntilReady && !isProviderReady) {
    return <>{fallback}</>;
  }

  if (enabled) {
    return <>{children}</>;
  }

  if (mode === 'disable') {
    return cloneElement(children, { disabled: true });
  }

  return <>{fallback}</>;
}

export function FeatureGate({
  suspendUntilReady = true,
  fallback = null,
  ...props
}: FeatureGateProps) {
  const clientStatus = useOpenFeatureClientStatus();
  const isProviderReady = clientStatus === ProviderStatus.READY;

  if (suspendUntilReady) {
    return (
      <Suspense fallback={fallback}>
        <FeatureGateInner
          {...props}
          suspendUntilReady={suspendUntilReady}
          fallback={fallback}
          isProviderReady={isProviderReady}
        />
      </Suspense>
    );
  }

  return (
    <FeatureGateInner
      {...props}
      suspendUntilReady={suspendUntilReady}
      fallback={fallback}
      isProviderReady={isProviderReady}
    />
  );
}

export interface TenantFlagRouteProps {
  flag: TenantFlagKey;
  redirectTo: string;
  children: ReactElement;
}

function TenantFlagRouteInner({ flag, redirectTo, children }: TenantFlagRouteProps) {
  const clientStatus = useOpenFeatureClientStatus();
  const tenantFlagResolutionReady = useTenantFlagResolutionReady();
  const enabled = useBooleanFlagValue(flag, flagDefaults[flag], { suspendUntilReady: true });

  if (!tenantFlagResolutionReady || clientStatus !== ProviderStatus.READY) {
    return null;
  }

  if (!enabled) {
    return <Navigate to={redirectTo} replace />;
  }

  return children;
}

export function TenantFlagRoute(props: TenantFlagRouteProps) {
  return (
    <Suspense fallback={null}>
      <TenantFlagRouteInner {...props} />
    </Suspense>
  );
}
